import { NumberFormat, Timestamp, WeekDays } from '@griply/types';
import {
  formatNumberShort,
  getTimeProgressChartTicks,
  getTimeProgressTickFormatter,
  interpolateDataEntries,
  isNumber,
  prepareTimeProgressEntries,
  TimeProgressTickRange,
} from '@griply/utils';
import { useParentSize } from '@visx/responsive';
import React, { memo, useCallback, useId, useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { characterWidth } from 'shared/assets/styles';
import { useToday } from 'shared/contexts/today';
import { DateEntry, XAxisTick } from 'shared/lib/recharts';
import { useTheme } from 'styled-components';

import { Dot } from './dot';
import * as Styled from './time-progress-chart.style';
import { useYAxisData } from './use-y-axis-data';

const CHART_HORIZONTAL_MARGIN = 16;
const DEFAULT_CHART_WIDTH = 320;

export type ProgressChartProps = {
  data: DateEntry[];
  startDate?: Timestamp | null;
  endDate?: Timestamp | null;
  chartStartDate?: Timestamp | null;
  chartEndDate?: Timestamp | null;
  startValue?: number | null;
  endValue?: number | null;
  weekStartsOn: WeekDays;
  completed?: boolean;
  isComplete?: boolean;
  hideYAxis?: boolean;
  showTodayCount?: boolean;
  showTodayLine?: boolean;
  hasSmallHeight?: boolean;
  height?: number;
  numberFormat?: NumberFormat;
};

export const TimeProgressChart: React.FC<ProgressChartProps> = memo(
  ({
    data,
    startDate,
    endDate,
    chartStartDate,
    chartEndDate,
    startValue,
    endValue,
    weekStartsOn,
    completed,
    isComplete,
    hideYAxis,
    showTodayCount,
    showTodayLine,
    hasSmallHeight,
    height,
    numberFormat,
  }) => {
    const theme = useTheme();
    const today = useToday();

    const instanceId = useId().replace(/[^a-zA-Z0-9]/g, '');
    const progressGradientId = `progress-${instanceId}`;
    const estimateGradientId = `estimate-${instanceId}`;

    const dataEntries = useMemo<DateEntry[]>(
      () =>
        prepareTimeProgressEntries(data, {
          startDate,
          startValue,
          today,
          completed,
          chartStartDate,
          chartEndDate,
        }),
      [
        chartEndDate,
        chartStartDate,
        completed,
        data,
        startDate,
        startValue,
        today,
      ],
    );

    const estimateData = useMemo(() => {
      if (!isNumber(endValue) || !startDate || !endDate) {
        return;
      }

      const data = [
        {
          date: startDate,
          value: startValue ?? 0,
        },
        { date: endDate, value: endValue! },
      ];

      const interpolatedData =
        chartStartDate && chartEndDate
          ? interpolateDataEntries(data, chartStartDate, chartEndDate)
          : data;

      return interpolatedData.map(({ date, value }) => ({
        date: date.getTime(),
        estimated: value,
      }));
    }, [
      chartEndDate,
      chartStartDate,
      endDate,
      endValue,
      startDate,
      startValue,
    ]);

    const { width: parentWidth, parentRef } = useParentSize({
      initialSize: { width: DEFAULT_CHART_WIDTH },
    });

    const {
      domain: yDomain,
      ticks: yTicks,
      maxCharacters: yMaxCharacters,
    } = useYAxisData(dataEntries, {
      startValue: startValue ?? undefined,
      endValue: endValue ?? undefined,
    });

    const yAxisWidth = yMaxCharacters * characterWidth;

    const availableWidth = Math.max(
      0,
      parentWidth - (hideYAxis ? 0 : yAxisWidth) - CHART_HORIZONTAL_MARGIN,
    );

    const {
      domain: xDomain,
      ticks: xTicks,
      tickRange,
      yearBoundaries,
    } = useMemo(
      () =>
        getTimeProgressChartTicks({
          entries: dataEntries,
          startDate,
          endDate,
          chartStartDate,
          chartEndDate,
          today,
          completed,
          weekStartsOn,
          availableWidth,
        }),
      [
        dataEntries,
        startDate,
        endDate,
        chartStartDate,
        chartEndDate,
        today,
        completed,
        weekStartsOn,
        availableWidth,
      ],
    );

    const dateFormatter = useMemo(
      () => getTimeProgressTickFormatter(tickRange),
      [tickRange],
    );

    const xFormatter = (tick: number) => dateFormatter(new Date(tick));

    const yFormatter = useCallback(
      (value: number) => formatNumberShort(value, numberFormat?.decimal || '.'),
      [numberFormat],
    );

    return (
      <Styled.ChartWrapper ref={parentRef}>
        <ResponsiveContainer
          width="100%"
          height={height ?? (hasSmallHeight ? 116 : 172)}
        >
          <AreaChart
            data={dataEntries}
            margin={{ top: 8, bottom: 8, left: 8, right: 8 }}
            style={{ cursor: 'inherit' }}
          >
            <defs>
              <Styled.ProgressGradient
                id={progressGradientId}
                $isComplete={isComplete}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" />
                <stop offset="100%" />
              </Styled.ProgressGradient>
              <Styled.EstimateGradient
                id={estimateGradientId}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" />
                <stop offset="100%" />
              </Styled.EstimateGradient>
            </defs>

            <CartesianGrid
              vertical={false}
              strokeDasharray="2 2"
              stroke={theme.palette.divider}
            />

            <YAxis
              domain={yDomain}
              axisLine={false}
              tickLine={false}
              ticks={yTicks}
              tickFormatter={yFormatter}
              width={hideYAxis ? 0 : yAxisWidth}
              style={{
                ...theme.typography.overline,
                fill: theme.palette.grey[600],
              }}
            />
            <XAxis
              dataKey="date"
              domain={xDomain}
              type="number"
              scale="time"
              tick={<XAxisTick dateFormatter={xFormatter} />}
              tickMargin={0}
              ticks={xTicks}
              tickLine={false}
              tickFormatter={xFormatter}
              padding={{ left: 16, right: 16 }}
              stroke={theme.palette.divider}
              interval={
                tickRange === TimeProgressTickRange.Month
                  ? 'preserveStartEnd'
                  : 0
              }
            />

            {yearBoundaries.map((boundary) => (
              <ReferenceLine
                key={boundary}
                x={boundary}
                stroke={theme.palette.divider}
                strokeDasharray="2 2"
              />
            ))}

            {!!estimateData?.length && (
              <Area
                dataKey="estimated"
                data={estimateData}
                dot={false}
                activeDot={false}
                stroke={theme.palette.grey[400]}
                strokeWidth={1}
                fill={`url(#${estimateGradientId})`}
                fillOpacity={1}
                baseValue={yDomain[0]}
                clipPath="none"
                isAnimationActive={false}
              />
            )}

            {!!startDate && startDate.getTime() !== xDomain[0] && (
              <ReferenceLine
                x={startDate.getTime()}
                stroke={theme.palette.info.main}
              />
            )}

            {!!endDate && (
              <ReferenceLine
                x={endDate.getTime()}
                stroke={theme.palette.error.main}
              />
            )}

            {!!showTodayLine && (
              <ReferenceLine
                x={today.getTime()}
                stroke={theme.palette.primary.main}
                strokeDasharray="2 2"
              />
            )}

            <Area
              type="linear"
              dataKey="value"
              stroke={
                isComplete
                  ? theme.palette.success.dark
                  : theme.palette.info.dark
              }
              strokeWidth={2}
              fill={`url(#${progressGradientId})`}
              fillOpacity={1}
              dot={
                <Dot
                  data={dataEntries}
                  yAxisWidth={yAxisWidth}
                  showCount={showTodayCount}
                  isComplete={isComplete}
                />
              }
              baseValue={yDomain[0]}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Styled.ChartWrapper>
    );
  },
);
